/**
 * G-80: one still from a live camera preview, as a JPEG the API can take.
 *
 * `BarcodeScanner` never needed this — its decoders read the video element
 * directly — so the photo search is the first kiosk surface to keep a frame.
 * Draws at the stream's intrinsic size, scaled so the longest edge is at most
 * `maxDim`: the tablet's 1080p preview lands around 1600 px and ~300 KB,
 * which is what the server keeps as the sample (OD-26 keeps originals; the
 * model sees a smaller copy made server-side).
 */
import { t } from "../i18n/t";

export async function captureStillFromVideo(
  video: HTMLVideoElement,
  maxDim = 1600,
): Promise<Blob> {
  const sourceW = video.videoWidth;
  const sourceH = video.videoHeight;
  if (!sourceW || !sourceH) {
    throw new Error(t("camera.noFrameYet"));
  }
  const scale = Math.min(1, maxDim / Math.max(sourceW, sourceH));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(sourceW * scale);
  canvas.height = Math.round(sourceH * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error(t("camera.couldNotDraw"));
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.9),
  );
  if (!blob) throw new Error(t("camera.couldNotEncode"));
  return blob;
}
